/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { useState, useEffect, useRef } from 'react'
import { X, Edit3, Save, Eye } from 'lucide-react'
import ReactMarkdown from 'react-markdown'

interface FileViewModalProps {
  filePath: string
  content: string
  onClose: () => void
  onSave?: (filePath: string, newContent: string) => Promise<void>
  highlightLines?: { start: number; end: number } // Line range to highlight (1-based)
}

export function FileViewModal({ filePath, content, onClose, onSave, highlightLines }: FileViewModalProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editedContent, setEditedContent] = useState(content)
  const [isSaving, setIsSaving] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const highlightRef = useRef<HTMLDivElement>(null)
  const isMarkdown = filePath.endsWith('.md')

  const handleSave = async () => {
    if (!onSave) return

    setIsSaving(true)
    try {
      await onSave(filePath, editedContent)
      setIsEditing(false)
    } catch (error) {
      console.error('Failed to save file:', error)
      alert('Failed to save file. See console for details.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setEditedContent(content)
    setIsEditing(false)
  }

  // Auto-scroll to highlighted line when modal opens
  useEffect(() => {
    if (highlightLines && highlightRef.current) {
      setTimeout(() => {
        highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 100)
    }
  }, [highlightLines])

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-background-secondary border border-border rounded-lg w-full max-w-5xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-title truncate">{filePath}</h2>
            <p className="text-xs text-foreground-muted">
              {content.length.toLocaleString()} characters • {content.split('\n').length.toLocaleString()} lines
            </p>
          </div>
          <div className="flex items-center gap-2 ml-4">
            {isMarkdown && !isEditing && (
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="btn-secondary px-3 py-2 text-sm flex items-center gap-2"
                title={showPreview ? 'Show raw text' : 'Preview formatted'}
              >
                <Eye className="w-4 h-4" />
                {showPreview ? 'Raw' : 'Preview'}
              </button>
            )}
            {onSave && (
              <>
                {isEditing ? (
                  <>
                    <button
                      onClick={handleCancel}
                      className="btn-secondary px-3 py-2 text-sm flex items-center gap-2"
                      disabled={isSaving}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      className="btn-primary px-3 py-2 text-sm flex items-center gap-2"
                      disabled={isSaving}
                    >
                      <Save className="w-4 h-4" />
                      {isSaving ? 'Saving...' : 'Save'}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="btn-primary px-3 py-2 text-sm flex items-center gap-2"
                  >
                    <Edit3 className="w-4 h-4" />
                    Edit
                  </button>
                )}
              </>
            )}
            <button
              onClick={onClose}
              className="btn-ghost p-2"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 min-h-0">
          {isEditing ? (
            <textarea
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              className="input w-full min-h-[400px] font-mono text-sm resize-y"
              spellCheck={false}
              autoFocus
            />
          ) : showPreview && isMarkdown ? (
            <div className="prose max-w-none">
              {highlightLines && (
                <div className="mb-4 p-3 bg-accent/10 border border-accent/30 rounded-lg text-sm">
                  <p className="text-accent font-semibold mb-1">📍 Citation Highlighted</p>
                  <p className="text-foreground-muted">
                    Lines {highlightLines.start}-{highlightLines.end} are referenced in this citation.
                    Switch to "Raw" view to see exact line highlighting.
                  </p>
                </div>
              )}
              <ReactMarkdown
                components={{
                  code: ({ inline, children, ...props }: any) => {
                    if (inline) {
                      return <code {...props}>{children}</code>
                    }
                    return (
                      <pre>
                        <code {...props}>{children}</code>
                      </pre>
                    )
                  },
                }}
              >
                {content}
              </ReactMarkdown>
            </div>
          ) : (
            <div className="text-sm font-mono">
              {content.split('\n').map((line, index) => {
                const lineNumber = index + 1
                const isHighlighted = highlightLines &&
                  lineNumber >= highlightLines.start &&
                  lineNumber <= highlightLines.end
                const isFirstHighlight = isHighlighted && lineNumber === highlightLines?.start

                return (
                  <div
                    key={index}
                    ref={isFirstHighlight ? highlightRef : null}
                    className={`whitespace-pre-wrap break-words ${isHighlighted ? 'bg-accent/20 border-l-2 border-accent pl-2' : ''
                      }`}
                  >
                    <span className="text-foreground-muted select-none mr-4 inline-block w-12 text-right">
                      {lineNumber}
                    </span>
                    {line}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border flex items-center justify-between text-xs text-foreground-muted">
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <Edit3 className="w-3 h-3" />
                <span>Edit Mode</span>
              </>
            ) : showPreview && isMarkdown ? (
              <>
                <Eye className="w-3 h-3" />
                <span>Preview Mode (Formatted Markdown)</span>
              </>
            ) : (
              <>
                <Eye className="w-3 h-3" />
                <span>View Mode{isMarkdown ? ' (Raw Text)' : ''}</span>
              </>
            )}
          </div>
          <div>
            {isEditing && editedContent !== content && (
              <span className="text-yellow-400">● Unsaved changes</span>
            )}
            {highlightLines && !showPreview && (
              <span className="text-accent">
                📍 Lines {highlightLines.start}-{highlightLines.end} highlighted
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

